// src/lib/time/dates.ts
import { SIGN_OFF_DUE_HOUR, TIME_ZONE } from "@/lib/time/constants";

/**
 * A calendar date as "YYYY-MM-DD". Time entry is about calendar days, so all math here is
 * on dates at UTC midnight and never depends on the machine's zone.
 */
export type IsoDate = string;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function toUtc(date: IsoDate): Date {
  return new Date(`${date}T00:00:00Z`);
}

function fromUtc(value: Date): IsoDate {
  return value.toISOString().slice(0, 10);
}

export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const parsed = toUtc(value);
  return !Number.isNaN(parsed.getTime()) && fromUtc(parsed) === value;
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = toUtc(date);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

/** 0 = Sunday ... 6 = Saturday. */
export function dayOfWeek(date: IsoDate): number {
  return toUtc(date).getUTCDay();
}

export function isSunday(date: IsoDate): boolean {
  return isIsoDate(date) && dayOfWeek(date) === 0;
}

/** The Sunday that starts the Sun-Sat week containing `date` (TE-38, warehouse week periods). */
export function weekStartOf(date: IsoDate): IsoDate {
  return addDays(date, -dayOfWeek(date));
}

/** The `?week=` search param: a real "YYYY-MM-DD" Sunday, otherwise nothing (this week). */
export function parseWeekParam(value: unknown): IsoDate | undefined {
  return typeof value === "string" && isSunday(value) ? value : undefined;
}

export function weekEndOf(date: IsoDate): IsoDate {
  return addDays(weekStartOf(date), 6);
}

export function weekDates(weekStart: IsoDate): IsoDate[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

export function monthStartOf(date: IsoDate): IsoDate {
  return `${date.slice(0, 7)}-01`;
}

export function monthEndOf(date: IsoDate): IsoDate {
  const d = toUtc(monthStartOf(date));
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0);
  return fromUtc(d);
}

/** The 1st of the month `months` away from the month `date` is in (negative = earlier). */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const d = toUtc(monthStartOf(date));
  d.setUTCMonth(d.getUTCMonth() + months);
  return fromUtc(d);
}

/**
 * The Entries Month view of a week: the 1st of the month the week ends in, the month the week
 * belongs to (TE-35), so Sep 27 - Oct 3 shows October.
 */
export function entriesMonthOf(weekStart: IsoDate): IsoDate {
  return monthStartOf(addDays(weekStart, 6));
}

/** Today's calendar date in `timeZone` ("en-CA" formats as YYYY-MM-DD). */
export function todayInZone(now: Date, timeZone: string = TIME_ZONE): IsoDate {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** True for a date after the Saturday that ends today's week (rule 10: warn, never block). */
export function isAfterCurrentWeek(date: IsoDate, today: IsoDate): boolean {
  return date > weekEndOf(today);
}

const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});
const WEEKDAY = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short" });
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "long",
  year: "numeric",
});

/** "Oct 11 – 17, 2026", "Sep 27 – Oct 3, 2026", "Dec 27, 2026 – Jan 2, 2027". */
export function formatWeekRange(weekStart: IsoDate): string {
  const end = addDays(weekStart, 6);
  const startYear = weekStart.slice(0, 4);
  const endYear = end.slice(0, 4);
  const startLabel = MONTH_DAY.format(toUtc(weekStart));
  if (startYear !== endYear) {
    return `${startLabel}, ${startYear} – ${MONTH_DAY.format(toUtc(end))}, ${endYear}`;
  }
  if (weekStart.slice(5, 7) !== end.slice(5, 7)) {
    return `${startLabel} – ${MONTH_DAY.format(toUtc(end))}, ${endYear}`;
  }
  return `${startLabel} – ${Number(end.slice(8, 10))}, ${endYear}`;
}

/** "Sun 11" for the grid header. */
export function formatDayHeader(date: IsoDate): string {
  return `${weekdayShort(date)} ${Number(date.slice(8, 10))}`;
}

/** "Wed". */
export function weekdayShort(date: IsoDate): string {
  return WEEKDAY.format(toUtc(date));
}

/** "Mon, Oct 12" for the Entries list. */
export function formatEntryDate(date: IsoDate): string {
  return `${weekdayShort(date)}, ${MONTH_DAY.format(toUtc(date))}`;
}

/** "Sun, Oct 18", for a period that is not open yet ("Opens Sun, Oct 18"). */
export function formatOpensOn(date: IsoDate): string {
  return formatEntryDate(date);
}

/** "September 2026" for a month sign-off row. */
export function formatMonth(date: IsoDate): string {
  return MONTH_YEAR.format(toUtc(date));
}

/** Offset of `timeZone` from UTC at `instant`, in minutes (EDT = -240, EST = -300). */
function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return Math.round((asUtc - instant.getTime()) / 60000);
}

/** The UTC instant at which `date` at `hour`:00 local time occurs in `timeZone` (DST-correct). */
function zonedInstant(date: IsoDate, hour: number, timeZone: string = TIME_ZONE): Date {
  const naive = toUtc(date).getTime() + hour * 3_600_000;
  const first = naive - zoneOffsetMinutes(new Date(naive), timeZone) * 60_000;
  const second = naive - zoneOffsetMinutes(new Date(first), timeZone) * 60_000;
  return new Date(second);
}

/**
 * When a week's sign-off becomes late: Monday 2:00 PM America/New_York after the week's
 * Saturday `weekEnd`, as a UTC instant (18:00 UTC in EDT, 19:00 UTC in EST).
 */
export function weekSignOffDueInstant(weekEnd: IsoDate): Date {
  return zonedInstant(addDays(weekEnd, 2), SIGN_OFF_DUE_HOUR);
}
